/**
 * @file zendesk.js
 * @description Central utility for all Zendesk Messaging Web Widget interactions.
 *
 * ARCHITECTURE RULE:
 *   All Zendesk operations go through this module. The widget snippet
 *   (`ze-snippet`) is injected at runtime by `initZendesk(key)` once the key
 *   arrives from the API (the decrypted `chatProvider` payload) — NOT at build
 *   time. This module drives both injection and the runtime API.
 *
 * MODE:
 *   Anonymous only — no authenticated identity (no JWT). We never call loginUser.
 *   Chat config (departments, pre-chat form, routing) is managed Zendesk-side in
 *   Admin Center, not here.
 *
 * API (Messaging): https://developer.zendesk.com/api-reference/widget-messaging/web/core/
 *   zE('messenger', 'show' | 'hide' | 'open' | 'close')
 *   zE('messenger:set', 'locale', '<code>')
 *
 * VISIBILITY STRATEGY (dual-layer, mirrors the Intercom approach):
 *   1. CSS  — `body.hide-chat-mobile` managed by ZohoChatButtonControl
 *   2. JS   — hideLauncher() / showLauncher() via zE('messenger', ...)
 */

import { mapToZendeskLocale } from 'utils/chatLocale'

const ZE_SNIPPET_ID = 'ze-snippet'

// ─────────────────────────────────────────────────────────────────────────────
// Snippet injection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Inject the Zendesk Messaging snippet using the key from the API.
 * Idempotent — safe to call multiple times; only injects once.
 *
 * @param {string} key - Zendesk widget key (from the API chatProvider payload).
 */
export const initZendesk = (key) => {
  if (!key) return
  if (document.getElementById(ZE_SNIPPET_ID)) return

  const script = document.createElement('script')
  script.id = ZE_SNIPPET_ID
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`
  script.async = true
  document.head.appendChild(script)
}

// ─────────────────────────────────────────────────────────────────────────────
// Guard / readiness
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true when the Zendesk Messaging SDK is loaded and callable.
 */
export const isZendeskReady = () => typeof window.zE === 'function'

/**
 * Run a callback once the Zendesk SDK is available.
 *
 * The snippet is injected with `defer`, so on first paint `window.zE` may not
 * exist yet. This polls briefly and invokes `cb` as soon as it does (or
 * immediately if already ready). Returns a cleanup function to cancel polling.
 *
 * @param {() => void} cb
 * @returns {() => void} cleanup
 */
export const onZendeskReady = (cb) => {
  if (isZendeskReady()) {
    cb()
    return () => {}
  }

  const interval = setInterval(() => {
    if (isZendeskReady()) {
      clearInterval(interval)
      cb()
    }
  }, 300)

  // Stop polling after ~15s to avoid leaking a timer if the snippet never loads.
  const timeout = setTimeout(() => clearInterval(interval), 15000)

  return () => {
    clearInterval(interval)
    clearTimeout(timeout)
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Launcher visibility
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hide the messaging launcher (and widget).
 * Pair with `document.body.classList.add('hide-chat-mobile')` for instant effect.
 */
export const hideLauncher = () => {
  if (isZendeskReady()) {
    window.zE('messenger', 'hide')
  }
}

/**
 * Show the messaging launcher.
 * Pair with `document.body.classList.remove('hide-chat-mobile')` for instant effect.
 */
export const showLauncher = () => {
  if (isZendeskReady()) {
    window.zE('messenger', 'show')
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat window
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Open the messaging widget, optionally setting the locale first.
 *
 * @param {string} [languageCode] - App language code (mapped to a Zendesk locale).
 */
export const openChat = (languageCode) => {
  if (!isZendeskReady()) return
  if (languageCode) {
    setLocale(languageCode)
  }
  window.zE('messenger', 'open')
}

// ─────────────────────────────────────────────────────────────────────────────
// Language
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Set the messaging widget locale. Overrides Zendesk's default browser-language
 * matching so the widget follows the user's selected app language.
 *
 * @param {string} languageCode - App language code (e.g. 'en', 'de', 'no').
 */
export const setLocale = (languageCode) => {
  if (isZendeskReady() && languageCode) {
    window.zE('messenger:set', 'locale', mapToZendeskLocale(languageCode))
  }
}
